/* eslint-disable unused-imports/no-unused-vars */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Column as RdgColumn, SortColumn } from "react-data-grid";
import { useLocation, useNavigate } from "react-router-dom";

import { TransactionsBlueIcon } from "../../../../assets";
import {
  ColorConstants,
  getInventoryEmptyGroupLabel,
  mapInventoryEmptyGroupFilterCriteria,
  ProjectIssuanceConstants,
  ProjectIssuanceTransactionConstants,
} from "../../../../constants";
import { CursorChangeProps, DataGridColumnCurrency, ResultType, SelectData } from "../../../../models";
import { IStateOrigin } from "../../../../navigators/navigateStateObjects";
import { SearchIssuanceTransactionsResponse } from "../../../../service/query";
import {
  getAggregateIssuances,
  searchInteractions,
  searchIssuanceTransactions,
} from "../../../../service/query/QueryService";
import { ResultData, Status } from "../../../../service/Shared";
import {
  UnitTransactionsPanelFilterObjects,
  UnitTransactionsPanelProjectSummary,
} from "../../../../templates/UnitTransactionsPanel/UnitTransactionsPanel";
import { buildCompanyCell } from "../../../../templates/UnitTransactionsPanel/utils";
import { useAuth } from "../../../../useAuth";
import {
  buildNutrientStackedChartData,
  buildSalesOverTimeChartData,
  flattenObject,
  getInventoryKeyColours,
  getInventoryUnitSuffix,
  getInventoryViewButtons,
  getNutrientDimensionColumns,
  getSalesOverTimeSeries,
  INVENTORY_AREA_HA_COLUMN,
  INVENTORY_GROUP_BY_COLUMN_1,
  INVENTORY_GROUP_BY_COLUMN_2,
  INVENTORY_GROUP_BY_COLUMN_3,
  INVENTORY_UNIT_COLUMNS,
  jsonReviver,
  moveCarbonToFront,
  NutrientChartSeries,
  recursivelyValidateAndTransformDateToString,
  SalesOverTimeSeries,
} from "../../../../utils";
import { getLandholdingDashboardRoute, getProjectDetailsByUuid } from "../../../../utils/routes";
import {
  ChartDatasets,
  DataGridColumnDefinition,
  DataGridCursorDataLoadEventHandler,
  DataGridIconCellFormatterData,
  DataGridLinkCellFormatterData,
  dataGridMapFilterCriteria,
  StackedChartData,
  Toast,
} from "../../../../widget";

// Fixed keys so a repeatedly failing lookup replaces its own toast rather than stacking
const INVENTORY_LOAD_ERROR_TOAST_KEY = "inventory-management-board-load-error";

const TAGS_FILTER_ERROR_TOAST_KEY = "inventory-management-board-tags-filter-error";

/**
 * The view buttons this surface offers for a measurement.
 *
 * The switch itself now lives in `getInventoryViewButtons`, shared with the landholding inventory,
 * which offers the same views and differs only in its default. This wrapper pins the surface so the
 * hook and its tests keep reading as a board concern.
 */
export const getBoardInventorySelectorButtons = (unitMeasurement: string): string[] =>
  getInventoryViewButtons("dashboard", unitMeasurement);

/**
 * The view a measurement opens on: the first of its buttons, so the selected view is always one the
 * selector offers. "By project" for Carbon, Biodiversity and Nutrient Neutrality, "Units per project"
 * for Overall and for Restoration, whose only view it is (KAN-9934).
 */
export const getBoardDefaultSelectOption = (unitMeasurement: string): string =>
  getBoardInventorySelectorButtons(unitMeasurement)[0] ?? "By project";

/*
  Selector label -> the aggregate query's groupBy key.

  Group-by keys deliberately do NOT compose - the server takes exactly one and rejects a second
  rather than build nonsense SQL - so a view showing a dimension AND the unit type beside it has to
  ask for a single COMPOSITE key. Nutrient Neutrality puts the nutrient next to four of its five
  dimensions, hence the four `...QuantityType` keys; they pack the nutrient into a second group-by
  slot exactly as `habitat` packs distinctiveness.

  "By nutrient" needs no composite: its dimension IS the nutrient, so it reuses the shipped
  `quantityType` key. "By region" keeps its own shipped measurement branch, untouched.

  Extracted from the hook for the same reason getBoardInventorySelectorButtons was: it is pure, and
  a wrong key here surfaces as a silent InvalidGroupBy from the server rather than a compile error.
*/
export const getBoardGroupCondition = (unitMeasurement: string, optionText: string): string => {
  const isNutrientNeutrality =
    unitMeasurement === ProjectIssuanceConstants.ISSUANCE_UNIT_MEASUREMENT_NUTRIENT_NEUTRALITY;

  const optionDict: { [key: string]: string } = {
    "Units per project": "projectNameMeasurement",
    "Revenue per project": "projectNameMeasurement",
    "By project": isNutrientNeutrality ? "projectNameQuantityType" : "projectName",
    "By nutrient": "quantityType",
    "By designated site": "designatedSiteQuantityType",
    "By habitat group": "habitat",
    "By condition": "habitatCondition",
    "By LPA": isNutrientNeutrality ? "lpaQuantityType" : "lpa",
    "By NCA": "nca",
    "By LNRS": isNutrientNeutrality ? "lnrsQuantityType" : "lnrs",
    "By region":
      unitMeasurement === ProjectIssuanceConstants.ISSUANCE_UNIT_MEASUREMENT_BIODIVERSITY ? "regionAreaHa" : "region",
    "By vintage": "vintageStartDate",
    "By type": "projectType",
    "By unit": "quantityType",
  };

  /*
    Overall ("") offers three views that are all project-scoped, so an unrecognised label there still
    resolves. With a measurement selected there is no such fallback: an unresolved label is a bug,
    and letting the server reject it is louder than silently grouping by something else.
  */
  if (optionDict[optionText] === undefined && unitMeasurement === "") return "projectNameMeasurement";

  return optionDict[optionText];
};

/**
 * The nutrient a row's drill-down scopes the transactions panel to.
 *
 * "By unit" (Carbon) and "By nutrient" (NN) group BY the quantity type, so it is the row's own
 * dimension value. The four composite NN views group by (dimension, nutrient), so the dimension
 * alone only half-identifies a row - without the nutrient the panel would also list the other
 * nutrient's transactions. "By project" reads slot 3 because slot 2 holds the project uuid its row
 * link needs; the other three read slot 2.
 *
 * Guarded on the measurement so Biodiversity's "By LPA" - whose slot 2 is a distinctiveness, not a
 * quantity type - is unaffected.
 */
export const getBoardDrillDownQuantityType = (
  unitMeasurement: string,
  selectorLabel: string,
  row: { groupByFieldContent1?: unknown; groupByFieldContent2?: unknown; groupByFieldContent3?: unknown }
): string | undefined => {
  if (selectorLabel === "By unit" || selectorLabel === "By nutrient") {
    return (row.groupByFieldContent1 as string) ?? undefined;
  }

  if (unitMeasurement !== ProjectIssuanceConstants.ISSUANCE_UNIT_MEASUREMENT_NUTRIENT_NEUTRALITY) return undefined;

  if (selectorLabel === "By project") return (row.groupByFieldContent3 as string) ?? undefined;

  if (selectorLabel === "By designated site" || selectorLabel === "By LPA" || selectorLabel === "By LNRS") {
    return (row.groupByFieldContent2 as string) ?? undefined;
  }

  return undefined;
};

/** The three unit series every inventory bar stacks, and the shade each takes from its palette. */
const BOARD_UNIT_SERIES: readonly NutrientChartSeries[] = [
  { label: "Available", key: "availableUnits", shade: "light", hover: "lightHover" },
  { label: "Reserved", key: "reservedUnits", shade: "mid", hover: "midHover" },
  { label: "Sold", key: "soldUnits", shade: "dark", hover: "darkHover" },
];

export interface BoardChartData {
  chartLabels: (string | string[])[];
  chartData: StackedChartData[];
  /** The nutrients drawn, in stack order, so the chart key can mirror them. Empty unless NN. */
  chartKeyNutrients: string[];
}

/**
 * The Nutrient Neutrality chart. The collapse-and-colour algorithm is shared with the landholding
 * inventory (buildNutrientStackedChartData); this supplies the board's three series and tells it
 * where each view hides the nutrient.
 *
 * The nutrient is read with getBoardDrillDownQuantityType, which already knows which group-by slot
 * each view puts it in - the drill-down and the chart cannot disagree about what a row's nutrient is.
 *
 * "By project" additionally collapses on the project uuid: display names carry no unique constraint,
 * so two same-named projects must stay two bars rather than one summed bar.
 */
const buildNutrientChartData = (
  rows: ResultData[],
  labels: (string | string[])[],
  unitMeasurement: string,
  selectorLabel: string
): BoardChartData =>
  buildNutrientStackedChartData(
    rows,
    labels,
    BOARD_UNIT_SERIES,
    (row) => getBoardDrillDownQuantityType(unitMeasurement, selectorLabel, row) ?? "",
    selectorLabel === "By project" ? (row) => (row.groupByFieldContent2 as string) ?? "" : undefined
  );

/**
 * Revenue per project: a single money series, one bar per row. The only view whose bars are a value
 * rather than a unit count, which is also why it is the only one that carries a currency prefix.
 */
const buildRevenueChartData = (rows: ResultData[], labels: (string | string[])[]): BoardChartData => ({
  chartLabels: labels,
  chartKeyNutrients: [],
  chartData: [
    {
      label: "Revenue",
      data: rows.map((item) => item.totalValue as number),
      backgroundColor: ColorConstants.GREEN_600,
      hoverBackgroundColor: ColorConstants.GREEN_500,
    },
  ],
});

/**
 * Every measurement other than Nutrient Neutrality: three flat series, one bar per row.
 *
 * The colour family swaps on the measurement rather than on anything in the data, via the shared
 * getInventoryKeyColours - Carbon INDIGO, Restoration MAGENTA and everything else GREEN - so the bars
 * and the chart key cannot drift. Rows are NOT collapsed by label here, deliberately: two projects
 * may share a display name (project_project.display_name carries no unique constraint), and merging
 * them into one bar would silently under-report both.
 */
const buildUnitsChartData = (
  rows: ResultData[],
  labels: (string | string[])[],
  unitMeasurement: string
): BoardChartData => {
  const colours = getInventoryKeyColours(unitMeasurement);

  return {
    chartLabels: labels,
    chartKeyNutrients: [],
    chartData: BOARD_UNIT_SERIES.map((series) => ({
      label: series.label,
      data: rows.map((item) => item[series.key] as number),
      backgroundColor: colours[series.shade],
      hoverBackgroundColor: colours[series.hover],
    })),
  };
};

/**
 * The stacked bar chart for a view, whichever of the three shapes it takes.
 *
 * A dispatcher rather than one generalised builder, because the three genuinely differ in kind and
 * share no body: they disagree on how many bars a row makes (nutrient collapses rows sharing a
 * dimension value, the other two are one bar per row), on how many series they emit (one, three, or
 * three per nutrient), and on where the colour comes from (fixed, the measurement, or a data value
 * via a palette allocator). Folding them together would not remove duplication - there is none - and
 * would push the nutrient collapse onto the shipped views, which is a real regression.
 *
 * Revenue is tested FIRST: it is an Overall view, so the measurement is "" and it can never be NN,
 * but ordering the check this way states that precedence rather than relying on it.
 *
 * "Sales over time" never reaches here - it is a LineChart fed by chartDatasets, a fourth shape.
 */
export const buildBoardChartData = (
  rows: ResultData[],
  labels: (string | string[])[],
  unitMeasurement: string,
  selectorLabel: string
): BoardChartData => {
  if (selectorLabel === "Revenue per project") return buildRevenueChartData(rows, labels);

  if (unitMeasurement === ProjectIssuanceConstants.ISSUANCE_UNIT_MEASUREMENT_NUTRIENT_NEUTRALITY) {
    return buildNutrientChartData(rows, labels, unitMeasurement, selectorLabel);
  }

  return buildUnitsChartData(rows, labels, unitMeasurement);
};

/*
  This surface hides Ecosystem recovery from the measurement dropdown; the landholding inventory
  deliberately does not. Carbon is pulled to the front so it sits directly under Overall.
*/
export const buildBoardMeasurementDropDownData = (measurementTypes: string[]): SelectData => {
  const dropDownSelectData = measurementTypes
    .filter(
      (measurementType: string) =>
        measurementType !== ProjectIssuanceConstants.ISSUANCE_UNIT_MEASUREMENT_ECOSYSTEM_RECOVERY
    )
    .map((v: string) => ({
      key: v,
      value: v,
    })) as SelectData;
  dropDownSelectData.unshift({ key: "", value: "Overall" });

  return moveCarbonToFront(dropDownSelectData);
};

interface UseInventoryManagementBoardReturnData {
  gridOnChange: DataGridCursorDataLoadEventHandler;
  hasEnoughDataForScrolling: boolean;
  isLoading: boolean;
  dropDownData: SelectData;
  selectedUnitMeasurementData: string;
  selectedStatuses: string[];
  handleStatusFilter: (values: string[]) => void;
  activeSelectOptionText: string;
  handleChangeSelectOption: (value: string) => void;
  pageData: InventoryManagementBoardPageData;
  kpiData: InventoryManagementBoardPageKpiData;
  columns: DataGridColumnDefinition[];
  initTagsFilterData: SelectData;
  tagsFilterData: SelectData;
  onTagsFilter: (keys: string[]) => Promise<void>;
  tagsFilterSelectedData?: string;
  hasUnits: boolean;
  prefix?: string;
  /** D-4 - " kg/yr" on Nutrient Neutrality only, appended to the y-axis ticks and the tooltip. */
  suffix?: string;
  handleUpdateSelectedUnitMeasurement: (value: string) => void;
  inventorySelectorButtons: string[];
  searchIssuanceTransactionsSortingCriteria: SortColumn[];
  searchIssuanceTransactionsColumns: DataGridColumnDefinition[];
  isSalesOverTimeSelected?: boolean;
  /** The measurements the "Sales over time" chart and its key show - those the measurement filter lists. */
  salesOverTimeSeries: SalesOverTimeSeries[];
  /** The line colours, matching pageData.chartDatasets one for one. */
  salesOverTimeColours: string[];
  unitTransactionsPanelFilter: UnitTransactionsPanelFilterObjects | undefined;
  unitTransactionsPanelProjectSummary: UnitTransactionsPanelProjectSummary | undefined;
  handleCloseUnitTransactionsPanel: () => void;
  refresh: boolean;
  handleRefresh: () => void;
  filterExportRows: (columns: ResultData[]) => ResultData[];
  filterExportColumns: (columns: RdgColumn<ResultData, unknown>[]) => RdgColumn<ResultData, unknown>[];
}

export interface InventoryManagementBoardPageKpiData {
  totalAvailableToSell: number;
  totalReserved: number;
  totalSold: number;
}

export interface InventoryManagementBoardPageData {
  chartData: StackedChartData[] | undefined;
  chartDatasets: ChartDatasets | undefined;
  chartLabels: (string | string[])[];
  /**
   * Nutrient Neutrality only - the nutrients the bars are coloured by, in stack order. Empty on
   * every other measurement. Optional because the landholding inventory reuses this type and has no
   * NN views of its own yet (KAN-9745).
   */
  chartKeyNutrients?: string[];
}

const searchIssuanceTransactionsColumns: DataGridColumnDefinition[] = [
  {
    name: "Transaction date",
    key: "transactionDate",
    dataType: "Date",
    formatter: "dateOnly",
    alignment: "left",
    sortable: true,
    filterable: false,
  },
  {
    name: "Project",
    key: "projectLink",
    dataType: "string",
    formatter: "link",
    filterable: true,
    sortable: true,
  },
  {
    name: "Landholding",
    key: "landholdingLink",
    dataType: "string",
    formatter: "link",
    filterable: true,
    sortable: true,
  },
  {
    name: "Verifier",
    key: "verifierName",
    dataType: "string",
    formatter: "stringWithEllipsis",
    filterable: true,
    sortable: true,
  },
  {
    name: "Quantity",
    key: "adjustedQuantity",
    dataType: "number",
    formatter: "number",
    sortable: true,
    filterable: true,
  },
  {
    name: "Measurement",
    key: "unitMeasurement",
    dataType: "string",
    formatter: "stringWithEllipsis",
    filterable: true,
    sortable: true,
  },
  {
    name: "Counterparty",
    key: "counterParty",
    dataType: "string",
    formatter: "buttonLink",
    filterable: true,
    sortable: true,
  },
  {
    name: "Broker",
    key: "broker",
    dataType: "string",
    formatter: "buttonLink",
    filterable: true,
    sortable: true,
  },
  {
    name: "Price",
    key: "price",
    dataType: "number",
    formatter: "currency",
    currency: DataGridColumnCurrency.Gbp,
    sortable: true,
    filterable: true,
  },
  {
    name: "Total value",
    key: "adjustedValue",
    dataType: "number",
    formatter: "currency",
    currency: DataGridColumnCurrency.Gbp,
    sortable: true,
    filterable: true,
  },
  {
    name: "Transaction reference",
    key: "externalReference",
    dataType: "string",
    formatter: "stringWithEllipsis",
    sortable: true,
    filterable: true,
  },
];
const searchIssuanceTransactionsSortingCriteria: SortColumn[] = [{ columnKey: "transactionDate", direction: "ASC" }];

const filterExportRows = (rows: ResultData[]): ResultData[] => {
  // Clone rows and Covert from date to string in dd/mm/yyyy format
  const rowsClone = recursivelyValidateAndTransformDateToString(JSON.parse(JSON.stringify(rows), jsonReviver))
    .data as ResultData[];
  rowsClone.forEach((row) => {
    // eslint-disable-next-line no-param-reassign
    if (row.transactionsButton !== undefined) delete row.transactionsButton;
    // The company cells hold a link object - export the name they display, not the object.
    // eslint-disable-next-line no-param-reassign
    if (row.counterParty !== undefined) row.counterParty = (row.counterParty as { text?: string })?.text ?? "";
    // eslint-disable-next-line no-param-reassign
    if (row.broker !== undefined) row.broker = (row.broker as { text?: string })?.text ?? "";
    // eslint-disable-next-line no-param-reassign
    if (row.averagePrice !== undefined) row.averagePrice = Number(row.averagePrice).toFixed(2);
  });
  return rowsClone;
};
const filterExportColumns = (rdgColumns: RdgColumn<ResultData, unknown>[]): RdgColumn<ResultData, unknown>[] => {
  return rdgColumns.filter((column) => column.key !== "transactionsButton");
};

export const useInventoryManagementBoard = (): UseInventoryManagementBoardReturnData => {
  const { currentUserType } = useAuth();
  const [dropDownData, setDropDownData] = useState<SelectData>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([
    ProjectIssuanceConstants.ISSUANCE_STATUS_ISSUED,
    ProjectIssuanceConstants.ISSUANCE_STATUS_DRAFT,
  ]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasEnoughDataForScrolling, setHasEnoughDataForScrolling] = useState(false);
  const [selectedUnitMeasurementData, setSelectedUnitMeasurementData] = useState<string>("");
  const [activeSelectOptionText, setActiveSelectOptionText] = useState<string>("By project");
  const [inventorySelectorButtons, setInventorySelectorButtons] = useState<string[]>([
    "Units per project",
    "Revenue per project",
    "Sales over time",
  ]);
  const [initTagsFilterData, setInitTagsFilterData] = useState<SelectData>([]);
  const [tagsFilterSelectedData, setTagsFilterSelectedData] = useState<string | undefined>();
  const [tagsFilterData, setTagsFilterData] = useState<SelectData>([]);
  const [tagsFilterIssuanceUuids, setTagsFilterIssuanceUuids] = useState<string[]>(); // For sales over time chart
  const [hasUnits, setHasUnits] = useState(false);
  const [unitTransactionsPanelFilter, setunitTransactionsPanelFilter] = useState<UnitTransactionsPanelFilterObjects>();
  const [unitTransactionsPanelProjectSummary, setUnitTransactionsPanelProjectSummary] =
    useState<UnitTransactionsPanelProjectSummary>();
  /*
    The project whose transactions panel is open, so the summary it was given can be refreshed from the
    next grid fetch - transactions change the project's available, reserved, sold and quoted figures. Held
    in a ref because the grid's onChange runs outside the render that opened the panel.
  */
  const openTransactionsPanelProjectUuidRef = useRef<string>();
  /*
    The nutrient that open panel's row was scoped to. NN's "By project" groups by (project, nutrient), so a
    project has one row per nutrient and the uuid alone no longer identifies the row that was opened -
    without this an open Phosphorus panel would be refreshed with the Nitrogen row's figures. Undefined on
    every view whose rows are one per project.
  */
  const openTransactionsPanelQuantityTypeRef = useRef<string>();

  const location = useLocation();
  const navigate = useNavigate();

  const [kpiData, setKpiData] = useState<InventoryManagementBoardPageKpiData>({
    totalAvailableToSell: 0,
    totalReserved: 0,
    totalSold: 0,
  });
  const [pageData, setPageData] = useState<InventoryManagementBoardPageData>({
    chartData: [],
    chartDatasets: [],
    chartLabels: [],
    chartKeyNutrients: [],
  });

  const [refresh, setRefresh] = useState(false);

  // Toggling the view remounts the grid, which fires a fresh gridOnChange while the previous
  // request may still be in flight. Only the most recent request is allowed to write chart,
  // KPI and loading state, otherwise a slow earlier response overwrites the current view's
  // data (and clears the other chart type's data, showing "No data to display").
  const chartRequestIdRef = useRef(0);

  const handleRefresh = (): void => {
    setRefresh(!refresh);
  };

  const handleCloseUnitTransactionsPanel = (): void => {
    openTransactionsPanelProjectUuidRef.current = undefined;
    openTransactionsPanelQuantityTypeRef.current = undefined;
    setunitTransactionsPanelFilter(undefined);
    setUnitTransactionsPanelProjectSummary(undefined);
  };

  useEffect(() => {
    setActiveSelectOptionText(getBoardDefaultSelectOption(selectedUnitMeasurementData));
  }, [selectedUnitMeasurementData]);

  // Any view change remounts the grid and starts a new request, so discard whatever is in
  // flight and show the loading state straight away rather than the half-updated chart.
  const startViewChange = (): void => {
    chartRequestIdRef.current += 1;
    setIsLoading(true);
  };

  const handleStatusFilter = (values: string[]): void => {
    if (values.join(",") === selectedStatuses.join(",")) return;
    startViewChange();
    setSelectedStatuses(values);
  };

  const handleChangeSelectOption = (value: string): void => {
    if (value === activeSelectOptionText) return;
    startViewChange();
    setActiveSelectOptionText(value);
  };

  const handleUpdateSelectedUnitMeasurement = (value: string): void => {
    if (value === selectedUnitMeasurementData) return;
    startViewChange();
    setSelectedUnitMeasurementData(value);
    setActiveSelectOptionText(getBoardDefaultSelectOption(value));
    setInventorySelectorButtons(getBoardInventorySelectorButtons(value));
  };

  const formatAggregateIssuancesData = useCallback(
    (responseData: ResultData[]): ResultData[] =>
      responseData.map((el) => {
        const result = flattenObject(el);

        // Rows reaching here have already had an empty group value replaced by its display label
        // ("No LPA"), so map it back before it is used as a filter value: the drill-down for that
        // bucket must ask for issuances with no value, not for one literally named "No LPA".
        const emptyGroupLabel = getInventoryEmptyGroupLabel(activeSelectOptionText);
        const groupValue =
          el.groupByFieldContent1 == null || el.groupByFieldContent1 === emptyGroupLabel
            ? null
            : (el.groupByFieldContent1 as string);

        // Only these views group by project, so they are the only ones whose row describes a single project.
        const isProjectScopedView = ["Units per project", "Revenue per project", "By project"].some(
          (x) => x === activeSelectOptionText
        );

        if (isProjectScopedView) {
          result.link = {
            text: el.groupByFieldContent1,
            to: getProjectDetailsByUuid(el.groupByFieldContent2?.toString() || "", currentUserType),
            state: { origin: { goBackText: "Back to inventory", from: location.pathname } as IStateOrigin },
          } as DataGridLinkCellFormatterData;
        }

        result.transactionsButton = {
          icon: <TransactionsBlueIcon />,
          action: () => {
            /*
              The summary needs the project's figures, which only a project-scoped row carries - the other
              groupings span projects, so they open the panel without one.
            */
            openTransactionsPanelProjectUuidRef.current = isProjectScopedView
              ? ((el.groupByFieldContent2 as string) ?? undefined)
              : undefined;
            openTransactionsPanelQuantityTypeRef.current = isProjectScopedView
              ? getBoardDrillDownQuantityType(selectedUnitMeasurementData, activeSelectOptionText, el)
              : undefined;
            setUnitTransactionsPanelProjectSummary(
              isProjectScopedView
                ? {
                    projectName: (el.groupByFieldContent1 as string) ?? "",
                    availableUnits: (el.availableUnits as number) ?? 0,
                    reservedUnits: (el.reservedUnits as number) ?? 0,
                    soldUnits: (el.soldUnits as number) ?? 0,
                    quotedUnits: (el.quotedUnits as number) ?? 0,
                  }
                : undefined
            );
            setunitTransactionsPanelFilter({
              projectUuid: isProjectScopedView ? (el.groupByFieldContent2 as string) : undefined,
              vintage:
                activeSelectOptionText === "By vintage" ? parseFloat(el.groupByFieldContent1 as string) : undefined,
              projectTypeDisplayName:
                activeSelectOptionText === "By type" ? (el.groupByFieldContent1 as string) : undefined,
              quantityType: getBoardDrillDownQuantityType(selectedUnitMeasurementData, activeSelectOptionText, el),
              habitatGroup:
                activeSelectOptionText === "By habitat group" ? (el.groupByFieldContent1 as string) : undefined,
              distinctiveness:
                activeSelectOptionText === "By habitat group" ? (el.groupByFieldContent2 as string) : undefined,
              habitatCondition:
                activeSelectOptionText === "By condition" ? (el.groupByFieldContent1 as string) : undefined,
              lpa: activeSelectOptionText === "By LPA" ? groupValue : undefined,
              nca: activeSelectOptionText === "By NCA" ? groupValue : undefined,
              lnrs: activeSelectOptionText === "By LNRS" ? groupValue : undefined,
              designatedSite: activeSelectOptionText === "By designated site" ? groupValue : undefined,
            });
          },
        } as DataGridIconCellFormatterData;

        if (el.landholdingUuid)
          result.landholdingLink = {
            text: el.landholdingName,
            to: getLandholdingDashboardRoute(result.landholdingUuid as string, currentUserType),
            state: { origin: { goBackText: "Back to inventory", from: location.pathname } as IStateOrigin },
          } as DataGridLinkCellFormatterData;

        return result;
      }) || [],
    /*
      The measurement is read inside the action (it decides which group-by slot holds the nutrient), so
      switching straight from Carbon's "By project" to Nutrient Neutrality's - where the label is unchanged -
      still has to rebuild these handlers.
    */
    [activeSelectOptionText, selectedUnitMeasurementData]
  );

  const formatIssuanceTransactionsData = useCallback(
    (responseData: SearchIssuanceTransactionsResponse | undefined): ResultData[] => {
      return (
        responseData?.results?.map((d) => {
          const result = flattenObject(d) as ResultData;

          result.counterParty = buildCompanyCell(d.counterParty, currentUserType, navigate);
          result.broker = buildCompanyCell(d.broker, currentUserType, navigate);

          result.projectLink = {
            text: d.project.displayName,
            to: getProjectDetailsByUuid(d.project.uuid, currentUserType),
            state: { origin: { goBackText: "Back to inventory", from: location.pathname } as IStateOrigin },
          } as DataGridLinkCellFormatterData;

          if (d.landholding.uuid)
            result.landholdingLink = {
              text: d.landholding.name,
              to: getLandholdingDashboardRoute(d.landholding.uuid, currentUserType),
              state: { origin: { goBackText: "Back to inventory", from: location.pathname } as IStateOrigin },
            } as DataGridLinkCellFormatterData;

          return result;
        }) || []
      );
    },
    [currentUserType, location.pathname, navigate]
  );

  // Common column definitions. The four below are shared with the landholding inventory.
  const groupByName1Column = INVENTORY_GROUP_BY_COLUMN_1;

  const groupByName2Column = INVENTORY_GROUP_BY_COLUMN_2;

  const groupByName3Column = INVENTORY_GROUP_BY_COLUMN_3;

  const sumAreaHaColumn = INVENTORY_AREA_HA_COLUMN;

  /** The group-by bases this surface passes to the shared Nutrient Neutrality column set. */
  const boardGroupByColumns = {
    name1: groupByName1Column,
    name2: groupByName2Column,
    name3: groupByName3Column,
  };

  const unitKmColumn: DataGridColumnDefinition = {
    name: "Unit size (Km)",
    key: "unitKm",
    dataType: "number",
    filterable: true,
    sortable: true,
  };

  const totalValueColumn: DataGridColumnDefinition = {
    name: "Total value",
    key: "totalValue",
    dataType: "number",
    formatter: "currency",
    currency: DataGridColumnCurrency.Gbp,
    filterable: true,
    sortable: true,
  };

  const landholdingColumn: DataGridColumnDefinition = {
    name: "Landholding",
    key: "landholdingLink",
    dataType: "string",
    formatter: "link",
    filterable: true,
    sortable: true,
  };

  const verifierColumn: DataGridColumnDefinition = {
    name: "Verifier",
    key: "verifierName",
    dataType: "string",
    formatter: "stringWithEllipsis",
    filterable: true,
    sortable: true,
  };

  /*
    The first five are shared with the landholding inventory; Avg. sale price and Transactions are
    this surface's own. Copied rather than referenced because an entry's insertColumnFunction splices
    into this array.
  */
  const commonColumns: DataGridColumnDefinition[] = [
    ...INVENTORY_UNIT_COLUMNS,
    {
      name: "Avg. sale price",
      key: "averagePrice",
      dataType: "number",
      formatter: "currency",
      currency: DataGridColumnCurrency.Gbp,
      filterable: true,
      sortable: true,
    },
    {
      name: "Transactions",
      key: "transactionsButton",
      dataType: "string",
      formatter: "icon",
      alignment: "center",
      filterable: false,
      sortable: false,
    },
  ];

  const columnsDict: Record<string, { columns: DataGridColumnDefinition[]; insertColumnFunction?: () => void }> = {
    "|Units per project": {
      columns: [
        { ...groupByName1Column, key: "link", name: "Project", formatter: "link" },
        landholdingColumn,
        verifierColumn,
        { ...groupByName3Column, name: "Measurement" },
      ],
    },
    "|Revenue per project": {
      columns: [
        { ...groupByName1Column, key: "link", name: "Project", formatter: "link" },
        landholdingColumn,
        verifierColumn,
        { ...groupByName3Column, name: "Measurement" },
      ],
      insertColumnFunction: () => {
        commonColumns.splice(6, 0, totalValueColumn);
      },
    },
    "|Sales over time": {
      columns: [
        { ...groupByName1Column, key: "link", name: "Project", formatter: "link" },
        { ...groupByName3Column, name: "Measurement" },
      ],
    },
    // Restoration's only view (KAN-9934): one row per project, as Carbon's "By project" renders it.
    [`${ProjectIssuanceConstants.ISSUANCE_UNIT_MEASUREMENT_RESTORATION}|Units per project`]: {
      columns: [{ ...groupByName1Column, key: "link", name: "Project", formatter: "link" }, landholdingColumn],
    },
    "Carbon|By project": {
      columns: [
        { ...groupByName1Column, key: "link", name: "Project", formatter: "link" },
        landholdingColumn,
        verifierColumn,
      ],
    },
    "Carbon|By vintage": {
      columns: [{ ...groupByName1Column, name: "Vintage" }],
    },
    "Carbon|By type": {
      columns: [{ ...groupByName1Column, name: "Project type" }],
    },
    "Carbon|By unit": {
      columns: [{ ...groupByName1Column, name: "Quantity type" }],
    },
    "Carbon|By region": {
      columns: [{ ...groupByName1Column, name: "Region" }],
    },
    "Biodiversity|By project": {
      columns: [
        { ...groupByName1Column, key: "link", name: "Project", formatter: "link" },
        landholdingColumn,
        verifierColumn,
        sumAreaHaColumn,
        unitKmColumn,
      ],
    },
    "Biodiversity|By habitat group": {
      columns: [
        { ...groupByName1Column, name: "Habitat group" },
        { ...groupByName2Column, name: "Distinctiveness" },
        sumAreaHaColumn,
        unitKmColumn,
      ],
    },
    "Biodiversity|By condition": {
      columns: [{ ...groupByName1Column, name: "Habitat condition" }, sumAreaHaColumn, unitKmColumn],
    },
    "Biodiversity|By LPA": {
      columns: [
        { ...groupByName1Column, name: ProjectIssuanceConstants.ISSUANCE_FIELD_LABELS.lpa },
        sumAreaHaColumn,
        unitKmColumn,
      ],
    },
    "Biodiversity|By NCA": {
      columns: [
        { ...groupByName1Column, name: ProjectIssuanceConstants.ISSUANCE_FIELD_LABELS.nca },
        sumAreaHaColumn,
        unitKmColumn,
      ],
    },
    "Biodiversity|By LNRS": {
      columns: [
        { ...groupByName1Column, name: ProjectIssuanceConstants.ISSUANCE_FIELD_LABELS.lnrs },
        sumAreaHaColumn,
        unitKmColumn,
      ],
    },
    "Biodiversity|By region": {
      columns: [{ ...groupByName1Column, name: "Region" }, sumAreaHaColumn, unitKmColumn],
    },
    /*
      Nutrient Neutrality. Each entry declares only its DIMENSION columns - gridGetColumns appends
      commonColumns, which already supplies Available / Reserved / Quoted / Sold / % sold /
      Avg. sale price / Transactions. So these give the final eight-column set.

      The entries come from getNutrientDimensionColumns, shared with the landholding inventory, which
      labels the same five views differently but requests the same group-by keys. Keyed there on the
      group-by key rather than on these labels, since the slot a nutrient arrives in is a property of
      the key.

      Deliberately absent: landholding, verifier, area and unit-length columns. Area and length are
      Biodiversity concepts, and Product's final column set excludes the other two.
    */
    "Nutrient Neutrality|By project": {
      columns: getNutrientDimensionColumns("projectNameQuantityType", boardGroupByColumns),
    },
    "Nutrient Neutrality|By nutrient": {
      columns: getNutrientDimensionColumns("quantityType", boardGroupByColumns),
    },
    "Nutrient Neutrality|By designated site": {
      columns: getNutrientDimensionColumns("designatedSiteQuantityType", boardGroupByColumns),
    },
    "Nutrient Neutrality|By LPA": {
      columns: getNutrientDimensionColumns("lpaQuantityType", boardGroupByColumns),
    },
    "Nutrient Neutrality|By LNRS": {
      columns: getNutrientDimensionColumns("lnrsQuantityType", boardGroupByColumns),
    },
  };

  const gridGetColumns = (
    unitMeasurement: string | undefined | null,
    selectOptionText: string | undefined | null
  ): DataGridColumnDefinition[] => {
    const dictKey = `${unitMeasurement || ""}|${selectOptionText || ""}`;
    const columnsDictEntry = columnsDict[dictKey];

    // Insert any additional columns into commonColumns if needed
    if (columnsDictEntry?.insertColumnFunction) columnsDictEntry.insertColumnFunction();

    return [...(columnsDictEntry?.columns || []), ...commonColumns];
  };

  const getGroupCondition = (optionText: string): string =>
    getBoardGroupCondition(selectedUnitMeasurementData, optionText);

  const gridOnChange = async ({ filtering, paging, sorting, isExport }: CursorChangeProps): Promise<ResultType> => {
    /*
      An export fetches the full result set through this same loader while a display load may still be in
      flight. It is tracked by the grid's own request id, so it must not claim this one: doing so would
      make the in-flight display request look stale and return the empty result the grid then renders.
    */
    if (!isExport) chartRequestIdRef.current += 1;
    const requestId = chartRequestIdRef.current;
    const isLatestRequest = (): boolean => isExport === true || requestId === chartRequestIdRef.current;

    if (!isExport) setIsLoading(true);
    const filterCriteria = dataGridMapFilterCriteria(filtering);

    let data: ResultType = {
      resultData: [],
      paging: {
        pageSize: paging.pageSize,
        totalCount: 0,
        startCursor: "",
        endCursor: "",
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };

    if (activeSelectOptionText === "Sales over time") {
      if (filterCriteria.projectLink) {
        filterCriteria.project = {
          displayName: filterCriteria.projectLink,
        };
        filterCriteria.projectLink = undefined;
      }

      if (filterCriteria.landholdingLink) {
        filterCriteria.landholding = {
          name: filterCriteria.landholdingLink,
        };
        filterCriteria.landholdingLink = undefined;
      }

      // The company columns display a link cell but filter on the company's name.
      if (filterCriteria.counterParty) {
        filterCriteria.counterParty = { name: filterCriteria.counterParty };
      }

      if (filterCriteria.broker) {
        filterCriteria.broker = { name: filterCriteria.broker };
      }

      if (tagsFilterIssuanceUuids && tagsFilterIssuanceUuids.length > 0) {
        filterCriteria.issuanceUuid = {
          operator: "in",
          value: tagsFilterIssuanceUuids,
        };
      }

      if (selectedStatuses.length > 0) {
        filterCriteria.issuanceStatus = {
          operator: "in",
          value: selectedStatuses,
        };
      }

      filterCriteria.type = {
        operator: "eq",
        value: ProjectIssuanceTransactionConstants.ISSUANCE_TRANSACTION_TYPE_SOLD,
      };

      if (!filterCriteria.adjustedQuantity) {
        filterCriteria.adjustedQuantity = {
          operator: "neq",
          value: 0,
        };
      }

      const sortKeyMap: Record<string, string> = {
        projectLink: "project.displayName",
        landholdingLink: "landholding.name",
        counterParty: "counterParty.name",
        broker: "broker.name",
      };

      await searchIssuanceTransactions({
        paging: {
          beforeCursor: paging.beforeCursor || null,
          afterCursor: paging.afterCursor || null,
          limit: paging.pageSize,
        },
        /* eslint-disable @typescript-eslint/no-explicit-any */
        sort: sorting.map((s: { key: any; direction: any }) => ({
          key: sortKeyMap[s.key] || s.key,
          direction: s.direction,
        })),
        filter: { results: filterCriteria },
      })
        .then((response) => {
          if (response.status === Status.Success && response.data && isLatestRequest()) {
            data = {
              resultData: formatIssuanceTransactionsData(response.data),
              paging: {
                startCursor: response.data?.paging?.startCursor || "",
                endCursor: response.data?.paging?.endCursor || "",
                pageSize: paging.pageSize || 10,
                totalCount: response.data?.paging?.total || 0,
                hasNextPage: response.data?.paging?.hasNextPage || false,
                hasPreviousPage: response.data?.paging?.hasPreviousPage || false,
              },
            };

            // Only the displayed rows drive the chart and counters - an export loads a different page size.
            if (!isExport) {
              const { chartLabels, chartDatasets } = buildSalesOverTimeChartData(response.data.results);
              setHasUnits(response.data.results.length > 0);
              setHasEnoughDataForScrolling(response.data.results.length >= 5);

              setPageData({
                chartDatasets,
                chartData: undefined,
                chartLabels,
                chartKeyNutrients: [],
              });
            }
          }
        })
        .finally(() => {
          if (!isExport && isLatestRequest()) setIsLoading(false);
        });
    } else {
      if (sorting.length === 0) {
        sorting.push({ key: "groupByFieldContent1", direction: "asc" });
      }

      const filterCondition = [];
      if (selectedUnitMeasurementData) {
        filterCondition.push({
          filterBy: "measurementType",
          filterOperator: "eq",
          filterValue: selectedUnitMeasurementData,
        });
      }
      if (selectedStatuses.length > 0) {
        filterCondition.push({
          filterBy: "issuanceStatus",
          filterOperator: "in",
          filterValue: `[${selectedStatuses.join(",")}]`,
        });
      }
      if (tagsFilterSelectedData) {
        filterCondition.push({
          filterBy: "tag",
          filterOperator: "in",
          filterValue: `[${tagsFilterSelectedData}]`,
        });
      }

      if (filterCriteria.landholdingLink) {
        filterCriteria.landholdingName = filterCriteria.landholdingLink;
        delete filterCriteria.landholdingLink;
      }

      if (filterCriteria.link) {
        filterCriteria.groupByFieldContent1 = filterCriteria.link;
        delete filterCriteria.link;
      }

      // The group cell shows an empty LPA/NCA/LNRS as "No LPA", so a filter typed against that label
      // has to be turned back into a null comparison before it reaches the server.
      if (filterCriteria.groupByFieldContent1) {
        filterCriteria.groupByFieldContent1 = mapInventoryEmptyGroupFilterCriteria(
          filterCriteria.groupByFieldContent1,
          activeSelectOptionText
        );
      }

      const sortKeyMap: Record<string, string> = {
        link: "groupByFieldContent1",
        landholdingLink: "landholdingName",
      };
      await getAggregateIssuances({
        filters: filterCondition,
        groupBy: [getGroupCondition(activeSelectOptionText)],
        isReadableProject: true,
        paging: {
          beforeCursor: paging.beforeCursor || null,
          afterCursor: paging.afterCursor || null,
          limit: paging.pageSize,
        },
        /* eslint-disable @typescript-eslint/no-explicit-any */
        sort: sorting.map((s: { key: any; direction: any }) => ({
          key: sortKeyMap[s.key] || s.key,
          direction: s.direction,
        })),
        filter: { results: filterCriteria },
      })
        .then((response) => {
          if (response.status === Status.Success && response.data && isLatestRequest()) {
            // The measurement dropdown belongs to the displayed view, not to an export load.
            if (!isExport) {
              setDropDownData(buildBoardMeasurementDropDownData(response.data.aspects.measurementTypes));
            }
            const chartLabelsArray: (string | string[])[] = [];
            const gridData: ResultData[] = [];
            response.data.results.forEach((result) => {
              let nameValue: any = result.groupByFieldContent1 ?? "";
              if (nameValue instanceof Date) {
                nameValue = nameValue.getFullYear().toString();
              }
              if (nameValue === "") {
                nameValue = getInventoryEmptyGroupLabel(activeSelectOptionText) ?? "";
              }

              let chartLabel = nameValue;
              if (activeSelectOptionText === "By habitat group") {
                chartLabel = [result.groupByFieldContent1, result.groupByFieldContent2];
              }

              gridData.push({
                groupByFieldContent1: nameValue,
                groupByFieldContent2: result.groupByFieldContent2,
                groupByFieldContent3: result.groupByFieldContent3,
                availableUnits: Number(result.availableUnits ?? 0),
                reservedUnits: Number(result.reservedUnits ?? 0),
                quotedUnits: Number(result.quotedUnits ?? 0),
                soldUnits: Number(result.soldUnits ?? 0),
                percentageSold: Number(result.percentageSold.toFixed(2) ?? 0),
                averagePrice: Number(result.averagePrice ?? 0),
                areaHa: Number(result.areaHa ?? 0),
                unitKm: Number(result.unitKm ?? 0),
                totalValue: Number(result.totalValue ?? 0),
                landholdingName: result.landholdingName,
                landholdingUuid: result.landholdingUuid,
                verifierName: result.verifierName,
              });
              chartLabelsArray.push(chartLabel);
            });

            // Chart, counters and the open panel reflect the displayed rows, so an export must not rewrite them.
            if (!isExport) {
              /*
                An open transactions panel holds the figures the row had when it was opened, and adding or
                editing a transaction refreshes this grid - so re-read that project's row and update it.
              */
              const openProjectUuid = openTransactionsPanelProjectUuidRef.current;
              const openQuantityType = openTransactionsPanelQuantityTypeRef.current;
              if (openProjectUuid) {
                const openProjectRow = response.data.results.find(
                  (r) =>
                    r.groupByFieldContent2 === openProjectUuid &&
                    (openQuantityType === undefined ||
                      getBoardDrillDownQuantityType(selectedUnitMeasurementData, activeSelectOptionText, r) ===
                        openQuantityType)
                );
                if (openProjectRow)
                  setUnitTransactionsPanelProjectSummary({
                    projectName: openProjectRow.groupByFieldContent1 ?? "",
                    availableUnits: Number(openProjectRow.availableUnits ?? 0),
                    reservedUnits: Number(openProjectRow.reservedUnits ?? 0),
                    soldUnits: Number(openProjectRow.soldUnits ?? 0),
                    quotedUnits: Number(openProjectRow.quotedUnits ?? 0),
                  });
              }

              const formatted = formatAggregateIssuancesData(response.data.results);
              // Set isUnits to true if there are any Units, false otherwise
              setHasUnits(formatted.length > 0);

              const chart = buildBoardChartData(
                gridData,
                chartLabelsArray,
                selectedUnitMeasurementData,
                activeSelectOptionText
              );

              setPageData({
                chartDatasets: undefined,
                chartData: chart.chartData,
                chartLabels: chart.chartLabels,
                chartKeyNutrients: chart.chartKeyNutrients,
              });
            }
            data.resultData.push(...formatAggregateIssuancesData(gridData));

            data.paging = {
              startCursor: response.data?.paging?.startCursor || "",
              endCursor: response.data?.paging?.endCursor || "",
              pageSize: paging.pageSize,
              totalCount: response.data?.paging?.total || 0,
              hasNextPage: response.data?.paging?.hasNextPage || false,
              hasPreviousPage: response.data?.paging?.hasPreviousPage || false,
            };

            // KPIs and the tag filter options belong to the displayed view, not to an export load.
            if (!isExport) {
              setKpiData({
                totalAvailableToSell: response.data.aspects.totalAvailableUnits || 0,
                totalReserved: response.data.aspects.totalReservedUnits || 0,
                totalSold: response.data.aspects.totalSoldUnits || 0,
              });

              const aspectsTags = (response.data.aspects.tags || []).map((v) => ({
                key: `${v.tagUuid}`,
                value: JSON.parse(v.tagData)?.name,
              }));
              setTagsFilterData(aspectsTags);
              if (tagsFilterData.length === 0) setInitTagsFilterData(aspectsTags);

              setHasEnoughDataForScrolling(response.data.results.length >= 5);
            }
          } else if (!isExport && isLatestRequest()) {
            /*
              A rejected request must not leave the previous view's chart on screen. The grid clears
              itself — `data` is only populated on success — but the chart is state, so it has to be
              cleared explicitly.
            */
            setHasUnits(false);
            setPageData({ chartData: [], chartLabels: [], chartDatasets: [], chartKeyNutrients: [] });
            Toast.error({ key: INVENTORY_LOAD_ERROR_TOAST_KEY, message: "Failed to load inventory" });
          }
        })
        .finally(() => {
          if (!isExport && isLatestRequest()) setIsLoading(false);
        });
    }

    return data;
  };

  const onTagsFilter = async (keys: string[]): Promise<void> => {
    if (activeSelectOptionText === "Sales over time") {
      startViewChange();

      if (keys.length === 0) {
        setTagsFilterIssuanceUuids(undefined);
      } else {
        const searchInteractionsFilterCriteria = dataGridMapFilterCriteria([]);

        searchInteractionsFilterCriteria.uuid = {
          operator: "in",
          value: keys,
        };

        try {
          const response = await searchInteractions({
            paging: {
              beforeCursor: null,
              afterCursor: null,
              limit: 50,
            },
            filter: { results: searchInteractionsFilterCriteria },
          });

          if (response.data?.results != null && response.data.results.length > 0) {
            setTagsFilterIssuanceUuids(
              response.data.results
                .filter((x) => x.objectInteractions.length > 0)
                .map((x) => x.objectInteractions.map((y) => y.objectUuid))
                .flat()
            );
          }
        } catch {
          /*
            startViewChange() has already put the page into its loading state and the view controls are
            disabled while it is set, so a failed lookup has to clear it here - the grid request that
            normally clears it is never started.
          */
          Toast.error({ key: TAGS_FILTER_ERROR_TOAST_KEY, message: "Failed to apply the tag filter" });
          setIsLoading(false);
          return;
        }
      }

      setTagsFilterSelectedData(undefined);
      handleRefresh();
    } else {
      const selected = keys.length > 0 ? keys.join(",") : undefined;

      // Only flag a reload when the grid key actually changes, otherwise nothing remounts
      // and the loading state would never be cleared.
      if (selected !== tagsFilterSelectedData) startViewChange();

      setTagsFilterSelectedData(selected);
      setTagsFilterIssuanceUuids(undefined);
    }
  };

  /*
    "Sales over time" draws only the measurements the measurement filter lists. Derived at render time
    from the dropdown state rather than inside gridOnChange, so the lines, their colours and the key are
    always cut from the same list.
  */
  const salesOverTimeSeries = useMemo(
    () => getSalesOverTimeSeries(dropDownData.map((option) => String(option.key))),
    [dropDownData]
  );

  const visiblePageData = useMemo(
    () => ({
      ...pageData,
      chartDatasets: pageData.chartDatasets?.filter((dataset) =>
        salesOverTimeSeries.some((series) => series.unitMeasurement === dataset.label)
      ),
    }),
    [pageData, salesOverTimeSeries]
  );

  const salesOverTimeColours = useMemo(() => salesOverTimeSeries.map((series) => series.colour), [salesOverTimeSeries]);

  return {
    hasUnits,
    gridOnChange,
    isLoading,
    hasEnoughDataForScrolling,
    dropDownData,
    selectedUnitMeasurementData,
    selectedStatuses,
    handleStatusFilter,
    activeSelectOptionText,
    handleChangeSelectOption,
    pageData: visiblePageData,
    kpiData,
    columns: gridGetColumns(selectedUnitMeasurementData, activeSelectOptionText),
    initTagsFilterData,
    tagsFilterData,
    onTagsFilter,
    tagsFilterSelectedData,
    prefix: activeSelectOptionText === "Revenue per project" ? "£" : undefined,
    /*
      D-4: Nutrient Neutrality quantities are an annual RATE, so the chart axis and tooltip carry the
      unit. Undefined on every other measurement and on Overall, leaving those charts byte-identical.
    */
    suffix: getInventoryUnitSuffix(selectedUnitMeasurementData),
    inventorySelectorButtons,
    handleUpdateSelectedUnitMeasurement,
    searchIssuanceTransactionsSortingCriteria,
    searchIssuanceTransactionsColumns,
    isSalesOverTimeSelected: activeSelectOptionText === "Sales over time",
    salesOverTimeSeries,
    salesOverTimeColours,
    unitTransactionsPanelFilter,
    unitTransactionsPanelProjectSummary,
    handleCloseUnitTransactionsPanel,
    refresh,
    handleRefresh,
    filterExportColumns,
    filterExportRows,
  };
};
